import type { PaginationChangedEvent } from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import type { AxiosError } from "axios";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SearchGridServerSideDatasource from "@/api/GridDataSources/SearchGridServerSideDataSource";
import { ErrorAlert, ExportButton } from "@/components";
import ColumnCustomizeFilter from "@/components/ColumnCustomizeFilter";
import DefaultGrid from "@/components/Common/DefaultGrid/DefaultGrid";
import { LOADING_TEXT, SEARCH_ERROR_MESSAGES } from "@/constants";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useSearchOnEnter } from "@/hooks/useSearchOnEnter";
import type QueryParams from "@/interfaces/QueryParams";
import type SearchGridSchema from "@/interfaces/SearchGridSchema";
import APIErrorHandling from "@/pages/error/ApiErrorHandlingPage";
import LoadingPage from "@/pages/LoadingPage";
import { SupplyChainColumnDefinitions } from "@/pages/search/Details/ColumnDefinitions";
import SearchForm from "@/pages/search/Details/Form";

const SearchPage: React.FC = () => {
  // Track page view once per session (professional approach)
  usePageTracking("Search Page");

  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [doSearchFn, setDoSearchFn] = useState<(() => void) | null>(null);

  const [searchParams, setSearchParams] = useState({
    commodity: [],
    country: [],
    itemDescription: "",
    itemNumber: undefined,
    vendorName: "",
    vendorNumber: undefined,
  } as QueryParams);

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportErrorStatus, setExportErrorStatus] = useState<
    AxiosError | { message?: string } | string
  >("");
  const [exportErrorType, setExportErrorType] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState<number | null>(null);
  const [showGrid, setShowGrid] = useState(false);
  const [noResultsFound, setNoResultsFound] = useState(false);

  const gridRef = useRef<AgGridReact<SearchGridSchema> | null>(null);
  const [searchTrigger, setSearchTrigger] = useState(0);

  const exportResultRowCount = useCallback(
    (count: number, hasActiveGridFilter: boolean) => {
      setRowCount(count);
      setNoResultsFound(count === 0 && !hasActiveGridFilter);
    },
    [],
  );

  const searchGridServerSideDatasource = useMemo(() => {
    return new SearchGridServerSideDatasource(
      searchParams,
      gridRef,
      exportResultRowCount,
      () => {
        setNoResultsFound(false);
        setRowCount(null);
        setShowGrid(false);
        setInlineError(SEARCH_ERROR_MESSAGES.SYSTEM_ERROR);
      },
    );
  }, [searchParams, searchTrigger, exportResultRowCount]);

  const handlePaginationPageSizeChanged = useCallback(
    (event: PaginationChangedEvent) => {
      const api = event.api;
      setTimeout(() => {
        if (api) {
          api.refreshCells();
          api.redrawRows();
        }
      }, 0);
    },
    [],
  );

  const handleSearchClick = useCallback(() => {
    setInlineError(null);
    setNoResultsFound(false);
    if (!showGrid) {
      setShowGrid(true);
    }
    setSearchTrigger((prev) => prev + 1);
  }, [showGrid]);

  const handleDoSearchChange = useCallback((fn: () => void) => {
    setDoSearchFn(() => fn);
  }, []);

  useSearchOnEnter({
    isEnabled: isSearchEnabled,
    onSearch: () => doSearchFn?.(),
    searchParams: searchParams,
  });

  if (loading) {
    return <LoadingPage title={LOADING_TEXT} />;
  }

  const renderAgGridTableWithCustomizeColumnView = () => {
    return (
      <div className="w-full">
        {showGrid && !noResultsFound && (
          <div>
            <div id="grid-control" className="section-header">
              <div id="column-customize-view-button" className="flex-item pr-8">
                <button
                  type="button"
                  className="flex flex-row place-content-end"
                  onClick={() => setIsOpen(!isOpen)}
                >
                  <div className="section-header__badge">
                    <div className="section-header__icon icon-filter_list" />
                  </div>
                  <p className="inline-block text-blue-600 px-4">
                    Customize Column View
                  </p>
                </button>
              </div>

              {rowCount !== 0 && (
                <div id="export-results-button" className="flex-item pr-8">
                  <ExportButton
                    mode="filtered"
                    searchParams={searchParams}
                    onExportError={(error) => {
                      setExportErrorType("exportCSV");
                      setExportErrorStatus(error as AxiosError);
                    }}
                  />
                </div>
              )}
              {/* Temporarily hidden per UX request; keep code for potential re-enable next quarter.
              <div id="export-button" className="flex-item pr-8">
                <ExportButton
                  mode="all"
                  onExportError={(error) => {
                    setExportErrorType("exportCSV");
                    setExportErrorStatus(error as AxiosError);
                  }}
                />
              </div>
              */}
            </div>
            <div className="pl-4 items-center justify-start">
              {isOpen && gridRef.current && (
                <ColumnCustomizeFilter
                  gridApi={gridRef.current.api}
                  onClickOutside={() => setIsOpen(false)}
                />
              )}
            </div>

            <div className="w-full shadow-md">
              <DefaultGrid
                columnDefs={SupplyChainColumnDefinitions}
                datasource={searchGridServerSideDatasource}
                loading={loading}
                onRowClicked={() => {}}
                onPaginationPageSizeChanged={handlePaginationPageSizeChanged}
                rowId={(params) => params.data._id ?? ""}
                noRowsFoundText={"No Record(s) Found"}
                quickFilterText={searchParams.itemDescription}
                ref={gridRef}
              />
            </div>
            <div className="h-10" />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="pb-2 mt-20 w-full flex justify-center 2xl:pt-6">
        <div className="w-full px-5">
          {inlineError && (
            <ErrorAlert
              message={inlineError}
              onDismiss={() => setInlineError(null)}
              className="mx-5 mb-4"
            />
          )}
          <SearchForm
            searchParams={searchParams}
            updateSearchParams={setSearchParams}
            handleSearchClick={handleSearchClick}
            searchCardExpanded={setIsSearchEnabled}
            onDoSearchChange={handleDoSearchChange}
            shouldKeepExpanded={noResultsFound || Boolean(inlineError)}
          />
        </div>
      </div>
      <div className="w-full px-5">
        {renderAgGridTableWithCustomizeColumnView()}
      </div>
      {noResultsFound && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="icon-search text-5xl text-blue-600 mb-4" />
          <h2 className="text-xl font-semibold text-blue-600 mb-2">
            No Results Found
          </h2>
          <p className="text-black-600">Try adjusting your search criteria.</p>
        </div>
      )}
      <div id="error-handling" className="w-full px-8">
        {!loading && exportErrorStatus && (
          <APIErrorHandling error={exportErrorStatus} type={exportErrorType} />
        )}
      </div>
    </div>
  );
};

export default SearchPage;
